import React, { useState, useRef } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Dialog,
    DialogContent,
    IconButton
} from '@mui/material';
import {
    CheckCircle,
    School,
    Code,
    Architecture,
    Speed,
    Security,
    Support,
    TrendingUp,
    Group,
    Star,
    PlayArrow,
    ArrowForward,
    Close,
    PlayCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoginOverlay from '../Authorization/LoginOverlay';
import SEOHead from '../SEO/SEOHead';

const LandingPage = () => {
    const navigate = useNavigate();
    const [openDemoSection, setOpenDemoSection] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showLoginOverlay, setShowLoginOverlay] = useState(false);
    const demoSectionRef = useRef(null);

    const features = [
        {
            icon: <Code sx={{ fontSize: 40, color: '#008080' }} />,
            title: "DSA Practice",
            description: "Master Data Structures & Algorithms with personalized practice sessions and AI-powered feedback."
        },
        {
            icon: <Architecture sx={{ fontSize: 40, color: '#008080' }} />,
            title: "System Design",
            description: "Learn Low-Level Design (LLD) and High-Level Design (HLD) with interactive AI interviews."
        },
        {
            icon: <School sx={{ fontSize: 40, color: '#008080' }} />,
            title: "Core Subjects",
            description: "Practice Operating Systems, Networking, DBMS, and more with comprehensive question banks."
        },
        {
            icon: <Speed sx={{ fontSize: 40, color: '#008080' }} />,
            title: "AI-Powered Learning",
            description: "Get instant feedback and guidance from our advanced AI system throughout your learning journey."
        }
    ];

    const howItWorks = [
        {
            step: "1",
            title: "Sign Up",
            description: "Create your account with Google OAuth for seamless access."
        },
        {
            step: "2",
            title: "Choose Your Path",
            description: "Select from DSA, System Design, or Core Subjects based on your goals."
        },
        {
            step: "3",
            title: "Practice & Learn",
            description: "Solve problems, get AI feedback, and track your progress."
        },
        {
            step: "4",
            title: "Improve",
            description: "Identify weak areas and focus on targeted improvement."
        }
    ];





    const stats = [
        { number: "10", label: "Active Learners" },
        { number: "24/7", label: "AI Support" }
    ];

    const demoVideos = {
        dsa: [
            { id: 1, title: "DSA Problem Solving", description: "Watch how to solve complex algorithms", videoUrl: "https://example.com/dsa1.mp4", thumbnail: "/images/dsa1-thumb.jpg" },
            { id: 2, title: "Data Structures Deep Dive", description: "Understanding arrays, linked lists, and trees", videoUrl: "https://example.com/dsa2.mp4", thumbnail: "/images/dsa2-thumb.jpg" },
            { id: 3, title: "Algorithm Optimization", description: "Tips and tricks for better performance", videoUrl: "https://example.com/dsa3.mp4", thumbnail: "/images/dsa3-thumb.jpg" }
        ],
        systemDesign: [
            { id: 4, title: "LLD Design Patterns", description: "Low-level design with design patterns", videoUrl: "https://example.com/lld1.mp4", thumbnail: "/images/lld1-thumb.jpg" },
            { id: 5, title: "HLD Architecture", description: "High-level system architecture design", videoUrl: "https://example.com/hld1.mp4", thumbnail: "/images/hld1-thumb.jpg" },
            { id: 6, title: "Scalability Patterns", description: "Building scalable systems", videoUrl: "https://example.com/scale1.mp4", thumbnail: "/images/scale1-thumb.jpg" }
        ],
        core: [
            { id: 7, title: "Operating Systems", description: "OS concepts and interview questions", videoUrl: "https://example.com/os1.mp4", thumbnail: "/images/os1-thumb.jpg" },
            { id: 8, title: "Database Design", description: "DBMS and database optimization", videoUrl: "https://example.com/db1.mp4", thumbnail: "/images/db1-thumb.jpg" },
            { id: 9, title: "Networking Fundamentals", description: "Computer networks and protocols", videoUrl: "https://example.com/net1.mp4", thumbnail: "/images/net1-thumb.jpg" }
        ]
    };

    const handleDemoClick = () => {
        demoSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
    };

    const handleCloseVideo = () => {
        setSelectedVideo(null);
    };

    return (
        <Box sx={{ backgroundColor: '#f8f9fa' }}>
            <SEOHead 
                title="SlazySloth - AI-Powered Tech Interview Practice | DSA, System Design & More"
                description="Master DSA, System Design (LLD/HLD), and Core CS subjects with AI-powered feedback. Practice coding interviews, get personalized guidance, and ace your tech interviews with confidence."
                keywords="tech interview practice, DSA practice, system design, coding interview, AI feedback, LLD, HLD, data structures, algorithms, computer science, interview preparation, programming interview, technical interview"
                url="https://slazysloth.com/"
                image="https://slazysloth.com/images/dashboard.png"
            />
            {/* Hero Section */}
            <Box sx={{ 
                backgroundColor: 'pink',
                color: 'black',
                py: 8,
                marginTop: '-2%'
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography component="h1" variant="h2" sx={{ 
                                fontWeight: 'bold', 
                                mb: 2,
                                fontSize: { xs: '2.5rem', md: '3.5rem' }
                            }}>
                                Master Tech Interviews with AI-Powered Practice
                            </Typography>
                            <Typography component="h2" variant="h5" sx={{ 
                                mb: 4, 
                                opacity: 0.9,
                                lineHeight: 1.4,
                                color: 'black'
                            }}>
                                Practice DSA, System Design (LLD/HLD), and Core CS Subjects with personalized AI feedback. 
                                Ace your coding interviews and technical interviews with confidence.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => setShowLoginOverlay(true)}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#008080',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0'
                                        }
                                    }}
                                >
                                    Start Practice
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={() => demoSectionRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                    sx={{
                                        borderColor: 'white',
                                        color: 'white',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            borderColor: '#f0f0f0',
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                        }
                                    }}
                                >
                                    Explore Features
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative'
                            }}>
                                {/* Dashboard Image */}
                                <Box sx={{
                                    width: '100%',
                                    maxWidth: '500px',
                                    height: '55.48%',
                                    maxHeight: '277.4px',
                                    borderRadius: '15px',
                                    overflow: 'hidden',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                                    position: 'relative',
                                    backgroundColor: '#f0f0f0'
                                }}>
                                    <img
                                        src="/images/dashboard.png"
                                        alt="Dashboard Preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            borderRadius: '15px'
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Stats Section */}
            <Box sx={{ py: 6, backgroundColor: 'white' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} justifyContent="center">
                        {stats.map((stat, index) => (
                            <Grid item xs={6} md={3} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#008080',
                                        mb: 1
                                    }}>
                                        {stat.number}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666' }}>
                                        {stat.label}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography component="h2" variant="h3" sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        mb: 2,
                        color: '#333'
                    }}>
                        Complete Tech Interview Preparation Platform
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        textAlign: 'center', 
                        mb: 6, 
                        color: '#666',
                        maxWidth: 600,
                        mx: 'auto'
                    }}>
                        Comprehensive tools and features designed to help you excel in technical interviews
                    </Typography>
                    
                    <Grid container spacing={4}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Card sx={{ 
                                    height: '100%',
                                    p: 3,
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                        transition: 'all 0.3s ease'
                                    }
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        {feature.icon}
                                        <Typography variant="h5" sx={{ 
                                            fontWeight: 'bold', 
                                            ml: 2,
                                            color: '#333'
                                        }}>
                                            {feature.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                                        {feature.description}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Features Showcase Section */}
            <Box ref={demoSectionRef} sx={{ py: 8, backgroundColor: 'white', color: '#333' }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{ 
                        textAlign: 'center', 
                        fontWeight: 'bold', 
                        mb: 2,
                        color: '#333'
                    }}>
                        Platform Features
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        textAlign: 'center', 
                        mb: 6, 
                        color: '#666',
                        maxWidth: 600,
                        mx: 'auto'
                    }}>
                        Explore the powerful tools and features that make learning effective
                    </Typography>
                    
                    <Grid container spacing={4}>
                        {/* Feature 1: AI Interviewer */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src="/images/ai-interview-2.png"
                                        alt="AI Interviewer"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#333',
                                        textAlign: 'center'
                                    }}>
                                        AI Interviewer
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Feature 2: DSA Editor */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src="/images/dsa_editor.png"
                                        alt="DSA Editor"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#333',
                                        textAlign: 'center'
                                    }}>
                                        DSA Editor
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Feature 3: HLD Diagram */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src="/images/hld_draw.png"
                                        alt="HLD Diagram"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#333',
                                        textAlign: 'center'
                                    }}>
                                        HLD Diagram
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Feature 4: LLD Code Editor */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src="/images/lld_code_editor.png"
                                        alt="LLD Code Editor"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#333',
                                        textAlign: 'center'
                                    }}>
                                        LLD Code Editor
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Feature 5: LLD Diagram */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src="/images/lld_class.png"
                                        alt="LLD Diagram"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#333',
                                        textAlign: 'center'
                                    }}>
                                        LLD Diagram
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Feature 6: DSA Sessions */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                    transition: 'all 0.3s ease'
                                }
                            }}>
                                <Box sx={{
                                    width: '100%',
                                    height: 200,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px 8px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src="/images/dsa_session.png"
                                        alt="DSA Sessions"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 'bold', 
                                        color: '#333',
                                        textAlign: 'center'
                                    }}>
                                        DSA Sessions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>



            {/* Demo Videos Section */}
            {openDemoSection && (
                <Box ref={demoSectionRef} sx={{ py: 8, backgroundColor: 'white' }}>
                    <Container maxWidth="lg">
                        <Typography variant="h3" sx={{ 
                            textAlign: 'center', 
                            fontWeight: 'bold', 
                            mb: 2,
                            color: '#333'
                        }}>
                            Watch Our Demo Videos
                        </Typography>
                        <Typography variant="h6" sx={{ 
                            textAlign: 'center', 
                            mb: 6, 
                            color: '#666',
                            maxWidth: 600,
                            mx: 'auto'
                        }}>
                            Explore our platform through these comprehensive demo videos
                        </Typography>

                        <Grid container spacing={6}>
                            {/* DSA Section */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Code sx={{ fontSize: 60, color: '#008080', mb: 2 }} />
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                                        DSA Practice
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666' }}>
                                        Master Data Structures & Algorithms
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ position: 'relative' }}>
                                    {demoVideos.dsa.map((video, index) => (
                                        <Box key={video.id}>
                                            <Card sx={{ 
                                                mb: 2, 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.3s ease'
                                                }
                                            }}
                                            onClick={() => handleVideoClick(video)}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Box sx={{
                                                            width: 60,
                                                            height: 40,
                                                            backgroundColor: '#f0f0f0',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <PlayArrow sx={{ color: '#008080' }} />
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                                {video.title}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                                {video.description}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                            
                                            {/* Connecting Line */}
                                            {index < demoVideos.dsa.length - 1 && (
                                                <Box sx={{
                                                    width: 2,
                                                    height: 20,
                                                    backgroundColor: '#008080',
                                                    mx: 'auto',
                                                    mb: 2
                                                }} />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>

                            {/* System Design Section */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Architecture sx={{ fontSize: 60, color: '#008080', mb: 2 }} />
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                                        System Design
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666' }}>
                                        LLD & HLD with AI Interviews
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ position: 'relative' }}>
                                    {demoVideos.systemDesign.map((video, index) => (
                                        <Box key={video.id}>
                                            <Card sx={{ 
                                                mb: 2, 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.3s ease'
                                                }
                                            }}
                                            onClick={() => handleVideoClick(video)}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Box sx={{
                                                            width: 60,
                                                            height: 40,
                                                            backgroundColor: '#f0f0f0',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <PlayArrow sx={{ color: '#008080' }} />
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                                {video.title}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                                {video.description}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                            
                                            {/* Connecting Line */}
                                            {index < demoVideos.systemDesign.length - 1 && (
                                                <Box sx={{
                                                    width: 2,
                                                    height: 20,
                                                    backgroundColor: '#008080',
                                                    mx: 'auto',
                                                    mb: 2
                                                }} />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>

                            {/* Core Subjects Section */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <School sx={{ fontSize: 60, color: '#008080', mb: 2 }} />
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                                        Core Subjects
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: '#666' }}>
                                        OS, Networking, DBMS & More
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ position: 'relative' }}>
                                    {demoVideos.core.map((video, index) => (
                                        <Box key={video.id}>
                                            <Card sx={{ 
                                                mb: 2, 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                                    transition: 'all 0.3s ease'
                                                }
                                            }}
                                            onClick={() => handleVideoClick(video)}
                                            >
                                                <CardContent sx={{ p: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Box sx={{
                                                            width: 60,
                                                            height: 40,
                                                            backgroundColor: '#f0f0f0',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <PlayArrow sx={{ color: '#008080' }} />
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                                                                {video.title}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                                {video.description}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                            
                                            {/* Connecting Line */}
                                            {index < demoVideos.core.length - 1 && (
                                                <Box sx={{
                                                    width: 2,
                                                    height: 20,
                                                    backgroundColor: '#008080',
                                                    mx: 'auto',
                                                    mb: 2
                                                }} />
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            )}

            {/* CTA Section */}
            <Box sx={{ 
                py: 8, 
                backgroundColor: 'linear-gradient(135deg, #008080 0%, #006666 100%)',
                background: 'linear-gradient(135deg, #008080 0%, #006666 100%)',
                color: 'white'
            }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ 
                            fontWeight: 'bold', 
                            mb: 3,
                            color: 'white'
                        }}>
                            Ready to Ace Your Interviews?
                        </Typography>
                        <Typography variant="h6" sx={{ 
                            mb: 4, 
                            opacity: 0.9,
                            color: 'white'
                        }}>
                            Join thousands of developers who are already improving their skills with AI-powered practice.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => setShowLoginOverlay(true)}
                            sx={{
                                backgroundColor: 'white',
                                color: '#008080',
                                px: 6,
                                py: 2,
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#f0f0f0'
                                }
                            }}
                        >
                            Get Started Now
                            <ArrowForward sx={{ ml: 1 }} />
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* Footer */}
            <Box sx={{ 
                py: 6, 
                backgroundColor: '#333',
                color: 'white'
            }}>
                <Container maxWidth="lg">
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 3
                    }}>
                        {/* Logo */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img
                                src="/images/logo_without_background.png"
                                alt="slazysloth"
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    marginRight: '12px'
                                }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                slazysloth
                            </Typography>
                        </Box>

                        {/* Discord Connect */}
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => window.open('https://discord.gg/bW7zF36Sa6', '_blank')}
                            sx={{
                                borderColor: '#008080',
                                color: '#008080',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderColor: '#006666',
                                    backgroundColor: 'rgba(0, 128, 128, 0.1)'
                                }
                            }}
                        >
                            Connect Discord
                        </Button>

                        {/* Copyright */}
                        <Typography variant="body2" sx={{ color: '#ccc' }}>
                            © 2024 slazysloth. All rights reserved.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Video Dialog */}
            <Dialog
                open={!!selectedVideo}
                onClose={handleCloseVideo}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <IconButton
                        onClick={handleCloseVideo}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.7)'
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                    {selectedVideo && (
                        <Box sx={{ width: '100%', height: '400px', backgroundColor: '#000' }}>
                            <Box sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white'
                            }}>
                                <Typography variant="h6">
                                    Video: {selectedVideo.title}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Login Overlay */}
            {showLoginOverlay && <LoginOverlay />}
        </Box>
    );
};

export default LandingPage;
